import React, { useState, useCallback, useMemo, useRef } from 'react'
import qs from 'query-string'
import startCase from 'lodash/startCase'
import { Link, navigate } from 'gatsby'

import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter'
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx'
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism'

SyntaxHighlighter.registerLanguage('jsx', jsx)

import {
  Avatar,
  bodyMap,
  hairMap,
  AvatarProps,
  clothingMap,
  eyesMap,
  eyebrowsMap,
  mouthsMap,
  facialHairMap,
  accessoryMap,
  hatMap,
  graphicsMap,
  theme,
} from 'beanheads'
import {
  Download,
  Code,
  Image,
  ArrowLeft,
  RefreshCw,
  ArrowRight,
  X,
} from 'react-feather'
import { SEO } from '../components/SEO'
import { getRandomOptions } from '../utils/getRandomOptions'

type SettingMaps = {
  [key in keyof AvatarProps]: any
}

const settingMaps: SettingMaps = {
  mask: { true: 'true', false: 'false' },
  faceMask: { true: 'true', false: 'false' },
  faceMaskColor: theme.colors.clothing,
  body: bodyMap,
  lipColor: theme.colors.lipColors,
  skinTone: theme.colors.skin,
  hair: hairMap,
  hairColor: theme.colors.hair,
  clothing: clothingMap,
  clothingColor: theme.colors.clothing,
  graphic: graphicsMap,
  eyes: eyesMap,
  eyebrows: eyebrowsMap,
  mouth: mouthsMap,
  facialHair: facialHairMap,
  accessory: accessoryMap,
  hat: hatMap,
  hatColor: theme.colors.clothing,
  lashes: { true: 'true', false: 'false' },
}

const ThoughtbaseAdCompact = () => {
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('thoughtbase-ad-dismissed') === 'true'
  })

  const handleDismiss = () => {
    setIsDismissed(true)
    if (typeof window !== 'undefined') {
      localStorage.setItem('thoughtbase-ad-dismissed', 'true')
    }
  }

  if (isDismissed) return null

  return (
    <div className="fixed right-0 top-0 p-4 z-50 max-w-xs">
      <div className="bg-white border rounded-lg p-4 shadow-lg relative">
        <div className="absolute top-0 right-0 p-4">
          <button
            onClick={handleDismiss}
            className=" text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center mb-2">
          <img src="/icon.svg" alt="Thoughtbase" className="w-8 h-8 mr-2" />
          <span className="font-semibold text-sm">Thoughtbase</span>
        </div>
        <p className="text-xs text-gray-700 mb-2 leading-relaxed">
          Collect and manage product feedback with Thoughtbase
        </p>
        <a
          href="https://thoughtbase.app?utm_source=beanheads&utm_medium=referral"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white text-xs bg-indigo-400 hover:bg-indigo-500 font-bold py-2 px-3 rounded inline-flex items-center"
        >
          Learn more <ArrowRight className="w-3 h-3 ml-1" />
        </a>
      </div>
    </div>
  )
}

function Editor({ location }: { location: Location }) {
  const svgRef = useRef<SVGSVGElement>(null)
  const props = useMemo<AvatarProps>(
    () => (location.search ? qs.parse(location.search) : getRandomOptions()),
    [location.search],
  )

  const updateProp = useCallback(
    e => {
      const name = e.target.name
      const value = e.target.value

      navigate(
        `/editor?${qs.stringify({
          ...props,
          [name]: value,
        })}`,
      )
    },
    [props],
  )

  const randomize = useCallback(() => {
    navigate(`/editor?${qs.stringify(getRandomOptions())}`)
  }, [])

  const [activeTab, setActiveTab] = useState<'react' | 'img'>('react')

  const download = () => {
    if (!svgRef.current) return

    const svgData = svgRef.current?.outerHTML
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const svgUrl = URL.createObjectURL(svgBlob)
    const downloadLink = document.createElement('a')
    downloadLink.href = svgUrl
    downloadLink.download = 'beanhead.svg'
    downloadLink.click()
  }

  const svgUrl = useMemo(
    () =>
      `/svg?${qs.stringify(
        Object.entries(props).reduce(
          (total, [key, value]) => ({ ...total, [key]: value }),
          {},
        ),
      )}`,
    [props],
  )

  const reactCode = `import { BeanHead } from 'beanheads'

const Example = () => (
  <BeanHead
    ${Object.entries(props)
      .map(([key, value]) =>
        [
          key,
          value === 'true'
            ? undefined
            : value === 'false'
            ? '{false}'
            : `"${value}"`,
        ]
          .filter(Boolean)
          .join('='),
      )
      .join('\n    ')}
  />
)
  `

  const imgCode = `<img src="https://beanheads.robertbroersma.com${svgUrl}" alt="Bean Head" />`

  if (typeof window === 'undefined') {
    return <SEO title="Bean Head Editor" />
  }

  return (
    <>
      <SEO title="Bean Head Editor" />
      <ThoughtbaseAdCompact />
      <div className="px-4 py-8">
        <div className="flex flex-col container mx-auto">
          <h1 className="text-4xl font-semibold text-center relative flex flex-col justify-center">
            <Link to="/" className="absolute">
              <ArrowLeft className="mr-4 h-8 w-8" />
            </Link>
            <span>Edit Bean Head</span>
            <span className="ml-auto" />
          </h1>

          <div className="max-w-xs w-full self-center mt-8">
            <Avatar
              ref={svgRef}
              {...props}
              lashes={props.lashes === 'true'}
              mask={props.mask === 'true'}
              faceMask={props.faceMask === 'true'}
            />
          </div>

          <div className="flex flex-col lg:flex-row w-full pt-12">
            <div className="flex-1">
              <h2 className="text-3xl font-semibold uppercase tracking-tight">
                Settings{' '}
                <button
                  onClick={randomize}
                  className="text-white text-lg bg-indigo-400 hover:bg-indigo-500 font-bold py-2 px-4 rounded inline-flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  <span>Random</span>
                </button>
              </h2>
              {Object.entries(settingMaps).map(([key, map]) => (
                <div key={key} className="flex items-center mt-4">
                  <label className="w-1/3 font-semibold" htmlFor="body">
                    {startCase(key)}
                  </label>
                  <select
                    id={key}
                    name={key}
                    defaultValue={`${props[key as keyof AvatarProps]}`}
                    onChange={updateProp}
                    className="w-full bg-white rounded border border-gray-400 focus:outline-none focus:border-purple-500 text-base px-4 py-2"
                  >
                    {Object.keys(map).map(value => (
                      <option key={value} value={value}>
                        {startCase(value)}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="w-16 h-16" />
            <div className="flex-1">
              <h2 className="text-3xl font-semibold uppercase tracking-tight">
                Usage
              </h2>
              <div className="w-full pb-12">
                <div className="sticky top-0 flex flex-col items-center">
                  <div className="w-full pt-4">
                    <div className="flex">
                      {/* <button
                        onClick={() => setActiveTab('react')}
                        className="text-white text-lg bg-indigo-400 hover:bg-indigo-500 font-bold py-2 px-4 rounded inline-flex items-center"
                      >
                        <Code className="w-4 h-4 mr-2" />
                        <span>React</span>
                      </button> */}
                      {/* <div className="w-2 h-2" /> */}
                      {/* <button
                        onClick={() => setActiveTab('img')}
                        className="text-white text-lg bg-indigo-400 hover:bg-indigo-500 font-bold py-2 px-4 rounded inline-flex items-center"
                      >
                        <Image className="w-4 h-4 mr-2" />
                        <span>Image</span>
                      </button> */}
                      {/* <div className="w-2 h-2" /> */}
                      <button
                        onClick={download}
                        className="text-white text-lg bg-indigo-400 hover:bg-indigo-500 font-bold py-2 px-4 rounded inline-flex items-center"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        <span>Download SVG</span>
                      </button>
                    </div>

                    {activeTab === 'react' && (
                      <div className="pt-2">
                        <SyntaxHighlighter style={atomDark}>
                          {`yarn add beanheads`}
                        </SyntaxHighlighter>
                        or
                        <SyntaxHighlighter style={atomDark}>
                          {`npm install beanheads --save`}
                        </SyntaxHighlighter>
                        then
                        <SyntaxHighlighter
                          style={atomDark}
                          language="javascript"
                        >
                          {reactCode}
                        </SyntaxHighlighter>
                      </div>
                    )}

                    {activeTab === 'img' && (
                      <div className="pt-2">
                        <SyntaxHighlighter
                          style={atomDark}
                          language="javascript"
                          wrapLines
                          codeTagProps={{
                            className: 'whitespace-pre-wrap break-all',
                          }}
                        >
                          {imgCode}
                        </SyntaxHighlighter>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Editor
